import type { Rules } from "../rules/types.ts";

/**
 * What a trip costs (ADR-137 decisions 19 and 20 and their amendment of 2026-10-28): coin by distance, and Focus by a
 * share of the pool. Pure whole-number maths on the rules' constants; the map server prices every offer and every
 * completion with it, and the page prints the figures the offer carries and never computes them.
 */

const SIXTEENTHS_PER_TILE = 16;
const PERCENT = 100;
const TILES_PER_FOCUS_STEP = 10;
const MAX_EXACT_SQUARED = Number.MAX_SAFE_INTEGER;

/**
 * The seven numbers the fare reads, each a rules constant: the coin base, the coin a tile and the coin cap (all copper),
 * the share of the Focus pool a trip from a shrine takes, and, from anywhere, the share's base, the percent it grows by
 * for each whole 10 tiles and its cap, in whole percents (ADR-137's amendment of 2026-10-28).
 */
export interface TravelKnobs {
  readonly coinBase: number;
  readonly coinPerTile: number;
  readonly coinCap: number;
  readonly focusPercent: number;
  readonly anywhereFocusPercent: number;
  readonly anywhereFocusPerTenTiles: number;
  readonly anywhereFocusCapPercent: number;
}

/**
 * Where a trip starts: standing at a shrine or a waypoint (`shrine`), or anywhere else (`anywhere`).
 */
export type TravelSource = "shrine" | "anywhere";

/**
 * What a price depends on: the source, the squared distance in sixteenths to the destination (`worldDistance`), whether
 * the destination is a Known-from-start `town` place (free of coin from a shrine), and the Focus pool's maximum.
 */
export interface TravelPriceInput {
  readonly source: TravelSource;
  readonly distanceSquared: number;
  readonly townFree: boolean;
  readonly focusMax: number;
}

/**
 * A trip's price: copper and Focus points, both whole numbers from 0.
 */
export interface TravelPrice {
  readonly coin: number;
  readonly focus: number;
}

/**
 * The fare's knobs a set of rules holds.
 *
 * @param rules The rules, or anything holding their constants.
 * @returns The seven knobs.
 * @example
 * travelKnobsOf(loadRules());
 * // { coinBase: 10, coinPerTile: 1, coinCap: 5000, focusPercent: 30,
 * //   anywhereFocusPercent: 20, anywhereFocusPerTenTiles: 1, anywhereFocusCapPercent: 90 }
 */
export function travelKnobsOf(rules: Pick<Rules, "constants">): TravelKnobs {
  const { constants } = rules;
  return {
    coinBase: constants.travel_coin_base,
    coinPerTile: constants.travel_coin_per_tile,
    coinCap: constants.travel_coin_cap,
    focusPercent: constants.travel_focus_percent,
    anywhereFocusPercent: constants.travel_anywhere_focus_percent,
    anywhereFocusPerTenTiles: constants.travel_anywhere_focus_per_10,
    anywhereFocusCapPercent: constants.travel_anywhere_focus_cap,
  };
}

/**
 * The whole tiles a squared distance in sixteenths reaches, a part of a tile counting as a whole one: the least `t` with
 * `(t * 16)^2 >= distanceSquared`, found in integers so no square root can land on the wrong side of a tile.
 *
 * @param distanceSquared The squared distance in squared sixteenths, a whole number from 0; a negative, non-finite or
 * unsafe value is read as farther than any fare reaches, so it is charged the cap.
 * @returns The distance in whole tiles, rounded up.
 */
function tilesCeil(distanceSquared: number): number {
  if (!Number.isFinite(distanceSquared) || distanceSquared < 0 || distanceSquared > MAX_EXACT_SQUARED) {
    return Number.MAX_SAFE_INTEGER;
  }
  let tiles = Math.ceil(Math.sqrt(distanceSquared) / SIXTEENTHS_PER_TILE);
  while (tiles > 0 && ((tiles - 1) * SIXTEENTHS_PER_TILE) ** 2 >= distanceSquared) {
    tiles -= 1;
  }
  while ((tiles * SIXTEENTHS_PER_TILE) ** 2 < distanceSquared) {
    tiles += 1;
  }
  return tiles;
}

/**
 * The share of the Focus pool a trip takes, in whole percents: the shrine's flat share, or from anywhere the base plus
 * the per-10-tiles percent for each whole 10 tiles of the distance, held to the cap.
 *
 * @param knobs The fare's knobs.
 * @param anywhere Whether the trip starts anywhere but a shrine.
 * @param tiles The distance in whole tiles, rounded up.
 * @returns Whole percents.
 */
function focusPercentOf(knobs: TravelKnobs, anywhere: boolean, tiles: number): number {
  if (!anywhere) {
    return knobs.focusPercent;
  }
  const percent = knobs.anywhereFocusPercent + knobs.anywhereFocusPerTenTiles * Math.floor(tiles / TILES_PER_FOCUS_STEP);
  return Math.min(percent, knobs.anywhereFocusCapPercent);
}

/**
 * The price of one trip (ADR-137 decisions 19 and 20 and their amendment of 2026-10-28). Coin is the base plus the
 * per-tile fare over the distance in whole tiles rounded up, held to the cap, the same from a shrine and from anywhere;
 * a trip from a shrine to a `town` place that is Known from the start costs no coin, a perk of the shrine alone. Focus
 * is the source's share of the pool's maximum (`focusPercentOf`), rounded up to a whole point; a Focus maximum that is
 * negative or not finite costs 0, and a distance that is negative or not finite is charged the coin cap and the Focus
 * cap.
 *
 * @param knobs The fare's knobs (`travelKnobsOf`).
 * @param input The source, distance, town flag and Focus maximum.
 * @returns The coin in copper and the Focus in points.
 * @example
 * travelCost(knobs, { source: "anywhere", distanceSquared: (500 * 16) ** 2, townFree: false, focusMax: 100 });
 * // { coin: 510, focus: 70 }
 */
export function travelCost(knobs: TravelKnobs, input: TravelPriceInput): TravelPrice {
  const tiles = tilesCeil(input.distanceSquared);
  const percent = focusPercentOf(knobs, input.source === "anywhere", tiles);
  const focus = Math.ceil((Number.isFinite(input.focusMax) ? Math.max(input.focusMax, 0) * percent : 0) / PERCENT);
  const coin = input.townFree && input.source === "shrine" ? 0 : Math.min(knobs.coinBase + knobs.coinPerTile * tiles, knobs.coinCap);
  return { coin, focus };
}
